import { authClient } from "@/lib/auth-client";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Clear session and send user to login (used on 401). */
async function clearAuthAndRedirectToLogin() {
  if (typeof window === "undefined") return;
  await authClient.signOut();
  window.location.href = "/login";
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };
  if (options.body !== undefined && options.body !== null) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });

  if (res.status === 401) {
    await clearAuthAndRedirectToLogin();
    const body = await res.json().catch(() => ({})) as { message?: string; error?: string };
    throw new ApiError(401, body.message || body.error || "Session expired. Please sign in again.");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { message?: string; error?: string; details?: unknown };
    const message = body.message || body.error || "Something went wrong";
    throw new ApiError(res.status, message, body.details);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export { ApiError };
